import React, { useState, useEffect, useRef } from 'react';
import { getJobs, deleteJob } from '../../services/adminApi';
import JobForm from './JobForm';
import './JobsList.css';

const JobsList = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingJob, setEditingJob] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const selectAllCheckboxRef = useRef(null);

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const data = await getJobs();
      setJobs(data);
    } catch (error) {
      console.error('Error loading jobs:', error);
      alert('Не удалось загрузить вакансии');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedJobs = [...jobs].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Handle special cases
    if (sortConfig.key === 'ACTIVE') {
      aValue = a.ACTIVE === 'Y' ? 1 : 0;
      bValue = b.ACTIVE === 'Y' ? 1 : 0;
    }

    // Handle string comparison
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
    }
    if (typeof bValue === 'string') {
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return '↕️';
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту вакансию?')) {
      return;
    }

    try {
      await deleteJob(id);
      loadJobs();
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error deleting job:', error);
      alert('Не удалось удалить вакансию');
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(new Set(sortedJobs.map(j => j.ID)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectItem = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    const count = selectedIds.size;
    if (!window.confirm(`Вы уверены, что хотите удалить ${count} ${count === 1 ? 'вакансию' : count < 5 ? 'вакансии' : 'вакансий'}?`)) {
      return;
    }

    setIsDeleting(true);
    try {
      const deletePromises = Array.from(selectedIds).map(id => deleteJob(id));
      await Promise.all(deletePromises);
      setSelectedIds(new Set());
      loadJobs();
    } catch (error) {
      console.error('Error bulk deleting jobs:', error);
      alert('Не удалось удалить выбранные вакансии');
    } finally {
      setIsDeleting(false);
    }
  };

  const isAllSelected = sortedJobs.length > 0 && selectedIds.size === sortedJobs.length;
  const isIndeterminate = selectedIds.size > 0 && selectedIds.size < sortedJobs.length;

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const handleEdit = (job) => {
    setEditingJob(job);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingJob(null);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingJob(null);
    loadJobs();
  };

  if (loading) {
    return <div className="loading">Загрузка вакансий...</div>;
  }

  return (
    <div className="admin-jobs-list">
      <div className="list-header">
        <div className="header-actions">
          {selectedIds.size > 0 && (
            <button
              onClick={handleBulkDelete}
              className="btn-danger"
              disabled={isDeleting}
            >
              {isDeleting ? 'Удаление...' : `Удалить выбранные (${selectedIds.size})`}
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <JobForm
          job={editingJob}
          onClose={handleFormClose}
        />
      )}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: '50px' }}>
                <input
                  type="checkbox"
                  ref={selectAllCheckboxRef}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  title="Выбрать все"
                />
              </th>
              <th className="col-id">ID</th>
              <th
                className="sortable col-name"
                onClick={() => handleSort('NAME')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Название {getSortIcon('NAME')}
              </th>
              <th className="col-img">Изображение</th>
              <th
                className="sortable col-status"
                onClick={() => handleSort('ACTIVE')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Активна {getSortIcon('ACTIVE')}
              </th>
              <th
                className="sortable col-sort"
                onClick={() => handleSort('SORT')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Сортировка {getSortIcon('SORT')}
              </th>
              <th className="col-actions">Действия</th>
            </tr>
          </thead>
          <tbody>
            {sortedJobs.map((job) => (
              <tr key={job.ID} className={selectedIds.has(job.ID) ? 'selected' : ''}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(job.ID)}
                    onChange={() => handleSelectItem(job.ID)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td className="col-id">{job.ID}</td>
                <td className="col-name">{job.NAME}</td>
                <td className="col-img">
                  {job.PREVIEW_PICTURE_SRC && (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL || 'https://www.kapussta.com'}${job.PREVIEW_PICTURE_SRC}`}
                      alt={job.NAME}
                      className="thumbnail"
                    />
                  )}
                </td>
                <td className="col-status">
                  <span className={`status ${job.ACTIVE === 'Y' ? 'active' : 'inactive'}`}>
                    {job.ACTIVE === 'Y' ? 'Активна' : 'Неактивна'}
                  </span>
                </td>
                <td className="col-sort">{job.SORT}</td>
                <td className="col-actions">
                  <button onClick={() => handleEdit(job)} className="btn-edit">Редактировать</button>
                  <button onClick={() => handleDelete(job.ID)} className="btn-delete">Удалить</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JobsList;
